import {
  changeDiscrepancySummaries,
  getDiscrepancySummaries,
  DiscrepancySummaryActionType,
} from '../ducks/discrepancySummary';
import {LoadingAdditional, AsyncResult, fromResult, Loading} from 'Core/models/Async';
import {put, call, takeLatest, all} from '@redux-saga/core/effects';
import {Page} from 'Core/models/Page';
import {DiscrepancySummary} from '../models/AccountDetail';
import {mergeWithNextPage} from 'Shared/utilities/PageUtility';
import accountApi from '../api/accountApi';
import {pipe} from 'fp-ts/lib/pipeable';
import * as E from 'fp-ts/lib/Either';
import {
  changeInvoiceDiscrepancies,
  getInvoiceDiscrepancies,
  InvoiceDiscrepanciesActionType,
} from '../ducks/invoiceDiscrepancies';
import { InvoiceDiscrepancyDto } from '../models/Invoice';
import { RequestMethod } from 'Shared/utilities/fetchHelpers';

export function* getDiscrepancySummariesFromApi(action: ReturnType<typeof getDiscrepancySummaries>) {
  if (action.payload.page) {
    action.payload.page.links.forEach(x => {
      if (!x.href.includes('api/employer/')) {
        x.href = 'api/employer/' + x.href;
      }
    });
    yield put(changeDiscrepancySummaries(new LoadingAdditional(action.payload.page)));
  } else {
    yield put(changeDiscrepancySummaries(new Loading(action.payload.page)));
  }

  const result: AsyncResult<Page<DiscrepancySummary>> = yield action.payload.page
    ? call(mergeWithNextPage, {existingPage: action.payload.page})
    : call(
        accountApi.getDiscrepancySummaries,
        action.payload.encryptedIdentifier,
        action.payload.accountNumber,
        action.payload.sourceId,
        action.payload.paymentDate,
        action.payload.discrepancySummaryFilter
      );

  yield pipe(
    result,
    E.map(fromResult),
    E.fold(
      value => put(changeDiscrepancySummaries(value)),
      value => put(changeDiscrepancySummaries(value))
    )
  );
}

export function* fetchDiscrepancySummariesSaga() {
  yield takeLatest(DiscrepancySummaryActionType.GET_DISCREPANCY_SUMMARIES, getDiscrepancySummariesFromApi);
}

function* getInvoiceDiscrepanciesFromApi(action: ReturnType<typeof getInvoiceDiscrepancies>) {
  const {accountNumber, invoiceLink, page} = action.payload;
  if (page) {
    page.links.forEach(x => {
      if (!x.href.includes('api/employer/')) {
        x.href = 'api/employer/' + x.href;
      }
    });
    yield put(changeInvoiceDiscrepancies(new LoadingAdditional(page)));
  } else {
    yield put(changeInvoiceDiscrepancies(new Loading(page)));
  }

  const result: AsyncResult<Page<InvoiceDiscrepancyDto>> = yield page
    ? call(mergeWithNextPage, {existingPage: page}, RequestMethod.POST, {accountNumber, invoiceLink})
    : call(accountApi.getInvoiceDiscrepancies, accountNumber, invoiceLink);

  yield pipe(
    result,
    E.map(fromResult),
    E.fold(
      value => put(changeInvoiceDiscrepancies(value)),
      value => put(changeInvoiceDiscrepancies(value))
    )
  );
}

export function* fetchInvoiceDiscrepanciesSaga() {
  yield takeLatest(InvoiceDiscrepanciesActionType.GET_INVOICE_DISCREPANCIES, getInvoiceDiscrepanciesFromApi);
}

export default function*() {
  yield all([fetchDiscrepancySummariesSaga(), fetchInvoiceDiscrepanciesSaga()]);
}
